/**
 * The rocket trooper (02 §9.1): one rocket, fired from 20-45 m after a 1.2 s aim that he
 * telegraphs with a shout (a `bark` of kind `grenade` - the player's cue to move). Too close,
 * he runs to open the range first, his back to you. Tube empty, he is an ordinary rifleman.
 *
 * The rocket goes where the ranger stood WHEN THE SHOUT WENT UP, not where he is at launch:
 * that is what makes the telegraph fair - hear it, and do not be there.
 *
 * Fields: `grenades` = rockets left (1, then 0); `peeking` = tube on the shoulder, aiming, with
 * `tacticTicks` the aim ticks remaining and `spotX, spotZ` the point aimed at. Rockets in
 * flight are `world.hazards.enemyRockets`.
 */

import { bark, makeNoise } from '../../alert';
import type { Ctx } from '../../ctx';
import { explode } from '../../damage';
import { rangerOnFoot, rangerSubmerged } from '../../hazards/terrain';
import { atan2 } from '../../math';
import { type Collider, SIGHT_BLOCK_HEIGHT } from '../../props';
import { clearSight } from '../../smoke';
import { segmentHit } from '../../statics';
import { ENEMY, RANGER, SPECIALS } from '../../tuning';
import { DT, type Enemy } from '../../types';
import { travel, turnToward } from '../movement';
import { EYE } from '../perception';
import { shootAtRanger } from '../shooting';
import { combatPreamble } from './common';

/** The rocket starts this far in front of him. */
const MUZZLE = 0.8;

function launch(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  const dx = e.spotX - e.x;
  const dz = e.spotZ - e.z;
  const d = Math.max(1.5, Math.sqrt(dx * dx + dz * dz));
  const dirX = dx / d;
  const dirZ = dz / d;
  w.hazards.enemyRockets.push({
    id: w.nextId++, x: e.x + dirX * MUZZLE, z: e.z + dirZ * MUZZLE, prevX: e.x, prevZ: e.z,
    dirX, dirZ, travelled: 0, reach: d - MUZZLE,
  });
  e.grenades = 0;
  e.sinceShot = 0;
  ctx.events.push({ type: 'rocket', x: e.x, z: e.z });
  makeNoise(ctx, e.x, e.z, SPECIALS.rocketNoise, 'gunfire', e.id);
}

/** Returns false once the tube is empty: the ordinary `stepCombat` takes him from there. */
export function stepRocketCombat(ctx: Ctx, e: Enemy): boolean {
  if (e.grenades <= 0) return false;
  const { world: w, statics } = ctx;
  const r = w.ranger;
  const dx = r.x - e.x;
  const dz = r.z - e.z;
  const dist = Math.sqrt(dx * dx + dz * dz);

  // He spotted the ranger with a soldier's eyes (perception.ts); once he is fighting, he keeps him in
  // view down the tube well past that range, for as long as the line stays clear.
  let tracking = e.seesRanger;
  if (!tracking && e.lostTicks <= SPECIALS.rocketTrackTicks && dist <= SPECIALS.rocketMaxRange) {
    const hidden = (r.inTrench && r.stance === 'crawl') || rangerSubmerged(w);
    if (!hidden && clearSight(ctx, e.x, e.z, EYE, r.x, r.z, r.stance === 'crawl' ? 0.4 : 1.5)) {
      tracking = true;
      e.lostTicks = 0;
      e.targetX = r.x;
      e.targetZ = r.z;
    }
  }

  if (combatPreamble(ctx, e)) {
    e.peeking = false;
    return true;
  }

  if (e.peeking) {
    // Aiming: rooted to the spot, sighted on the place he shouted at. The rocket goes whatever the ranger does.
    e.moving = false;
    turnToward(e, atan2(e.spotX - e.x, e.spotZ - e.z), 9);
    e.tacticTicks--;
    if (e.tacticTicks <= 0) {
      e.peeking = false;
      launch(ctx, e);
    }
    return true;
  }
  if (!tracking) {
    e.moving = false;
    return true;
  }
  if (dist < SPECIALS.rocketMinRange) {
    // Too close for the rocket: open the range, straight away from the ranger.
    const def = statics.def;
    const l = Math.max(0.01, dist);
    const want = SPECIALS.rocketMinRange + SPECIALS.rocketRangeMargin - dist;
    const ax = Math.min(def.sizeX - 2, Math.max(2, e.x - (dx / l) * want));
    const az = Math.min(def.sizeZ - 2, Math.max(2, e.z - (dz / l) * want));
    if (!travel(ctx, e, ax, az, ENEMY.huntSpeed, 1)) return true;
    // Cornered. At arm's length the sidearm; otherwise danger-close will have to do.
    if (dist < SPECIALS.rocketDangerClose) {
      turnToward(e, atan2(dx, dz), 9);
      shootAtRanger(ctx, e);
      return true;
    }
  }
  // The shout is the warning (02 §9.1): from here the player has the aim time, plus the flight, to move.
  e.moving = false;
  turnToward(e, atan2(dx, dz), 9);
  e.peeking = true;
  e.tacticTicks = SPECIALS.rocketAimTicks;
  e.spotX = r.x;
  e.spotZ = r.z;
  bark(ctx, e, 'grenade');
  return true;
}

/** Flight of the troopers' rockets: slower than a LAW, so the shout and the smoke trail can be outrun. */
export function stepEnemyRockets(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  const rockets = w.hazards.enemyRockets;
  // A man crawling in a trench is below its flight, as he is below any round.
  const exposed = rangerOnFoot(w) && !(r.inTrench && r.stance === 'crawl');
  const body: Collider = {
    propId: -1, shape: 'circle', x: r.x, z: r.z, r: RANGER.radius, hx: 0, hz: 0, h: 2,
    blocksMove: false, blocksSight: false, blocksBullets: true,
  };
  for (let i = rockets.length - 1; i >= 0; i--) {
    const k = rockets[i];
    if (!k) continue;
    const left = k.reach - k.travelled;
    const step = Math.min(SPECIALS.rocketSpeed * DT, Math.max(0, left));
    const nx = k.x + k.dirX * step;
    const nz = k.z + k.dirZ * step;
    let tHit = 2;
    // Fired from the shoulder: it clears sandbags and low cover, and stops on anything a man cannot see over.
    for (const c of statics.colliders) {
      if (!c.blocksSight || c.h < SIGHT_BLOCK_HEIGHT) continue;
      const hit = segmentHit(c, k.x, k.z, nx, nz);
      if (hit && hit.enter < tHit) tHit = hit.enter;
    }
    const struck = exposed ? segmentHit(body, k.x, k.z, nx, nz) : null;
    if (struck && struck.enter < tHit) tHit = struck.enter;
    if (tHit <= 1 || left <= SPECIALS.rocketSpeed * DT) {
      const t = Math.min(1, tHit);
      rockets.splice(i, 1);
      explode(ctx, k.x + (nx - k.x) * t, k.z + (nz - k.z) * t, SPECIALS.rocketBlast, 'grenade', false);
      continue;
    }
    k.prevX = k.x;
    k.prevZ = k.z;
    k.x = nx;
    k.z = nz;
    k.travelled += step;
    if (nx < 0 || nz < 0 || nx > statics.def.sizeX || nz > statics.def.sizeZ) rockets.splice(i, 1);
  }
}
